import React, { useMemo, useState, useEffect, useRef } from "react";
import PlayerBoard from "./PlayerBoard";
import ActionForm from "./ActionForm";
import ActionLog from "./ActionLog";
import RoundTimer from "./RoundTimer";
import NightOrder from "./NightOrder";
import ConfirmDialog from "./ConfirmDialog";
import RoleCardsModal from "./RoleCardsModal";
import { postAction, updatePlayer, endGame, deleteAction } from "../api";
import {
  ACTION_TYPES,
  eligibleActors,
  eligibleTargets,
  getCurrentNightStep,
  getNightQueue,
  roleSide,
  SIDE_LABELS,
} from "../roles";
import useWakeLock from "../useWakeLock";

function formatElapsed(seconds) {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

const DRAFT_DEFAULT = { type: ACTION_TYPES[0].id, actorId: "", targetId: "" };

// При відновленні гри (перезавантаження сторінки) раунд і фаза раніше завжди
// стартували з 1/"ніч", хоча гра з бекенду могла прийти вже на 3-му раунді —
// ведучий бачив "раунд 1", а нічна черга миттєво резолвилась і перемикала
// назад на день, бо всі дії того (насправді старого) раунду вже "виконані".
// Тому визначаємо стартові round/phase із самого журналу дій:
// - беремо найбільший round, що зустрічається в actions (1, якщо дій ще нема);
// - якщо в цьому раунді вже є денна дія (голосування/скіп дня з phase "day"),
//   значить раунд повністю відіграний і вже почалась нова ніч без жодної дії —
//   стартуємо на round+1 / "night";
// - інакше дивимось, чи нічна черга цього раунду вже повністю виконана: якщо
//   так — це день (ніч закінчилась, голосування ще не було), інакше — ще ніч.
function deriveInitialRoundAndPhase(game) {
  const actions = game?.actions || [];
  if (actions.length === 0) return { round: 1, phase: "night" };

  const maxRound = actions.reduce((m, a) => Math.max(m, Number(a.round) || 1), 1);
  const roundActions = actions.filter((a) => Number(a.round) === maxRound);
  const dayHappened = roundActions.some((a) => a.phase === "day");

  if (dayHappened) {
    return { round: maxRound + 1, phase: "night" };
  }

  const queue = getNightQueue(game?.players || [], actions, maxRound);
  const nightDone = queue.length > 0 && queue.every((step) => step.done);
  return { round: maxRound, phase: nightDone ? "day" : "night" };
}

export default function GameScreen({ game, setGame, onEndGame }) {
  const [round, setRound] = useState(() => deriveInitialRoundAndPhase(game).round);
  // Окремий текстовий стан для поля вводу раунду: дозволяє тимчасово порожнє
  // значення під час редагування, щоб не "прилипала" одиниця на початку
  // (напр. 1 -> 12 замість 1 -> 2) — типова проблема контрольованих
  // number-інпутів на мобільних клавіатурах.
  const [roundInput, setRoundInput] = useState(() =>
    String(deriveInitialRoundAndPhase(game).round)
  );
  const [phase, setPhase] = useState(() => deriveInitialRoundAndPhase(game).phase);
  const [busyPlayerId, setBusyPlayerId] = useState(null);
  const [submittingAction, setSubmittingAction] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [error, setError] = useState("");
  const [confirmState, setConfirmState] = useState(null);
  // Гравець, роль якого щойно перевірили (перевірка/сповідь) — показуємо
  // його одразу відкритою карткою ролі, тією ж, що й при роздачі ролей на
  // старті гри, без кроку "передайте пристрій".
  const [checkRevealPlayer, setCheckRevealPlayer] = useState(null);
  // Підказку про завершення гри показуємо на весь екран модалкою (а не
  // рядком у шапці), щоб ведучий точно її не пропустив. Ведучий може
  // закрити її й продовжити гру — вона з'явиться знову, лише коли підказка
  // зникне і зʼявиться знову (тобто зміниться баланс сторін).
  const [dismissedWinTone, setDismissedWinTone] = useState(null);

  // Стан форми дії тепер живе тут, а не в ActionForm — це дозволяє
  // PlayerBoard підставляти обраного гравця прямо тапом по картці замість
  // випадаючого списку.
  const [draft, setDraft] = useState(DRAFT_DEFAULT);
  const [pickField, setPickField] = useState(null); // 'actor' | 'target' | null
  // Поки true, тип дії вночі підлаштовується сам під поточний крок черги
  // ночі (див. ефект нижче). Вимикається, щойно ведучий сам обирає тип
  // вручну, і знову вмикається після успішного запису дії — так наступний
  // крок черги підставляється автоматично, але ручний вибір нічого не ламає.
  const [autoNightType, setAutoNightType] = useState(true);

  const aliveBoardRef = useRef(null);

  // Тримаємо екран увімкненим, поки триває гра — ведучий постійно в нього дивиться.
  useWakeLock(true);

  const createdAtMs = game.createdAt ? new Date(game.createdAt).getTime() : null;
  const [elapsedSeconds, setElapsedSeconds] = useState(() =>
    createdAtMs ? Math.floor((Date.now() - createdAtMs) / 1000) : null
  );

  // Тікаємо секундомір гри незалежно від того, чи гра щойно створена, чи
  // відновлена після перевідкриття браузера — в обох випадках маємо created_at.
  useEffect(() => {
    if (!createdAtMs) return;

    setElapsedSeconds(Math.floor((Date.now() - createdAtMs) / 1000));
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - createdAtMs) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [createdAtMs]);

  // Уночі сам підставляємо у форму тип дії та виконавця, що відповідають
  // поточному невиконаному кроку черги ночі (напр. спочатку "вбивство" з
  // доном як виконавцем — або мафією, якщо дона вже немає в живих, — після
  // запису дії наступний крок за NIGHT_STEPS підставляється так само, і так
  // далі). Спрацьовує, лише поки ведучий не вибрав актора/ціль вручну і не
  // змінив тип сам.
  useEffect(() => {
    if (phase !== "night" || !autoNightType) return;
    const alivePlayers = game.players.filter((p) => p.alive);
    const current = getCurrentNightStep(alivePlayers, game.actions, round);
    if (!current) return;
    const suggestedActorId = current.suggestedActorId ? String(current.suggestedActorId) : "";
    setDraft((prev) => {
      if (prev.targetId) return prev;
      if (prev.actorId && prev.actorId !== suggestedActorId) return prev;
      if (prev.type === current.actionTypeId && prev.actorId === suggestedActorId) return prev;
      return { type: current.actionTypeId, actorId: suggestedActorId, targetId: "" };
    });
  }, [phase, round, game.actions, game.players, autoNightType]);

  const alive = game.players.filter((p) => p.alive);
  const dead = game.players.filter((p) => !p.alive);

  // Щойно вся черга нічних дій цього раунду записана — сама перемикаємо
  // фазу на день. Якщо вночі взагалі нема кому ходити (усі спецролі вибули),
  // черга порожня і чекає ручного перемикання ведучим.
  useEffect(() => {
    if (phase !== "night") return;
    const alivePlayers = game.players.filter((p) => p.alive);
    const queue = getNightQueue(alivePlayers, game.actions, round);
    if (queue.length > 0 && queue.every((step) => step.done)) {
      resolveNightDeaths();
      setPhase("day");
    }
  }, [phase, round, game.actions, game.players]);

  // Розбивка сторін серед живих гравців — це те, що ведучий постійно рахує
  // в голові, і саме баланс сторін визначає завершення гри.
  const sideCounts = useMemo(() => {
    return alive.reduce((acc, p) => {
      const side = roleSide(p.role);
      acc[side] = (acc[side] || 0) + 1;
      return acc;
    }, {});
  }, [alive]);

  const townCount = sideCounts.town || 0;
  const mafiaCount = sideCounts.mafia || 0;
  const soloCount = sideCounts.solo || 0;

  // Підказка про завершення гри — орієнтовна, ведучий все одно вирішує сам.
  // Поки живий маніяк (соло-сторона) І лишається хоч одна з двох командних
  // сторін (мафія чи мирні), результат ще не вирішений: голосування "мафія
  // проти мирних" нічого не означає, доки маніяк може перебити розклад сил.
  // Тому раніше траплялось хибне "перемога мафії" навіть коли на столі ще
  // лишався живий маніяк і шанси мирних не були вичерпані.
  const winBanner = useMemo(() => {
    if (alive.length === 0) return null;

    if (soloCount > 0) {
      if (mafiaCount === 0 && townCount === 0) {
        return { tone: "solo", text: "Маніяк перемагає — він лишився сам на столі." };
      }
      return null;
    }

    if (mafiaCount === 0) {
      return { tone: "town", text: "Місто перемагає — мафії не залишилось." };
    }
    if (mafiaCount >= townCount) {
      return { tone: "mafia", text: "Мафія перемагає — вона зрівнялась або переважає решту столу." };
    }
    return null;
  }, [alive.length, mafiaCount, soloCount, townCount]);

  // Скидаємо "закрито ведучим" щойно підказка зникає — щоб при повторному
  // спрацюванні (інший баланс сторін) вона знову показалась модалкою.
  useEffect(() => {
    if (!winBanner) setDismissedWinTone(null);
  }, [winBanner]);

  const showWinModal = Boolean(winBanner) && dismissedWinTone !== winBanner.tone;

  // Гравці, доступні для вибору актора/цілі в поточному режимі вибору —
  // використовуються для підсвітки карток на дошці гравців.
  const pickCandidateIds = useMemo(() => {
    if (!pickField) return null;
    const list =
      pickField === "actor"
        ? eligibleActors(alive, draft.type)
        : eligibleTargets(alive, draft.type, draft.actorId);
    return new Set(list.map((p) => String(p.id)));
  }, [pickField, alive, draft.type, draft.actorId]);

  function handleTypeChange(nextType) {
    // Ручний вибір типу — тимчасово вимикаємо автопідстановку, щоб не
    // "відкочувала" вибір ведучого назад на поточний крок черги ночі.
    setAutoNightType(false);
    setDraft({ type: nextType, actorId: "", targetId: "" });
    setPickField(null);
  }

  function handlePickRequest(field) {
    setPickField((prev) => (prev === field ? null : field));
    if (aliveBoardRef.current) {
      aliveBoardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function handlePlayerPick(playerId) {
    if (!pickField) return;
    setDraft((prev) => ({
      ...prev,
      [pickField === "actor" ? "actorId" : "targetId"]: playerId,
    }));
    setPickField(null);
  }

  function handleFieldClear(field) {
    setDraft((prev) => ({ ...prev, [field === "actor" ? "actorId" : "targetId"]: "" }));
  }

  function requestKick(player) {
    setConfirmState({
      title: "Вигнати гравця",
      message: `Вигнати ${player.name} з гри? Якщо це помилка, дію можна одразу скасувати кнопкою "Скасувати останній запис" у журналі.`,
      tone: "danger",
      confirmLabel: "Вигнати",
      onConfirm: () => {
        setConfirmState(null);
        performKick(player);
      },
    });
  }

  async function performKick(player) {
    setBusyPlayerId(player.id);
    setError("");
    try {
      await updatePlayer(game.gameId, player.id, { alive: false });
      const saved = await postAction(game.gameId, {
        type: "vote_out",
        actorId: null,
        targetId: player.id,
        round,
        phase,
        note: null,
      });
      const savedAction = saved?.action || {
        id: `local-${Date.now()}`,
        type: "vote_out",
        actorId: null,
        targetId: player.id,
        round,
        phase,
        note: null,
      };
      setGame((prev) => ({
        ...prev,
        players: prev.players.map((p) =>
          p.id === player.id ? { ...p, alive: false } : p
        ),
        actions: [...prev.actions, savedAction],
      }));

      // Вигнання голосуванням — денна дія. Після неї день закінчується:
      // самі перемикаємо фазу на ніч і переходимо до наступного раунду.
      if (phase === "day") {
        setPhase("night");
        setAutoNightType(true);
        stepRound(1);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyPlayerId(null);
    }
  }

  async function handleAction(action) {
    setSubmittingAction(true);
    setError("");
    try {
      const saved = await postAction(game.gameId, action);
      const savedAction = saved?.action || { ...action, id: `local-${Date.now()}` };

      // Нічне вбивство більше НЕ вбиває гравця одразу: воно лише лягає в
      // журнал. Живим/мертвим гравець стає щойно ніч завершиться (див.
      // resolveNightDeaths) — це дає лікарю можливість вилікувати ціль
      // пізніше цієї ж ночі, навіть якщо вбивство вже записане.
      setGame((prev) => ({ ...prev, actions: [...prev.actions, savedAction] }));
      // Скидаємо вибір актора/цілі після успішного запису, щоб форма була
      // готова до наступної дії, і знову вмикаємо автоперемикання типу на
      // наступний крок нічної черги.
      setDraft((prev) => ({ ...prev, actorId: "", targetId: "" }));
      setAutoNightType(true);

      // Перевірка (комісар/сержант/дон/адвокат) чи сповідь (священник) —
      // одразу показуємо роль перевіреного гравця карткою ролі, як на
      // роздачі, тільки вже відкритою.
      if (savedAction.type === "check" || savedAction.type === "confess") {
        const checkedPlayer = game.players.find(
          (p) => String(p.id) === String(savedAction.targetId)
        );
        if (checkedPlayer) setCheckRevealPlayer(checkedPlayer);
      }
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setSubmittingAction(false);
    }
  }

  async function handleUndoLast() {
    const last = game.actions[game.actions.length - 1];
    if (!last) return;

    setUndoing(true);
    setError("");
    try {
      if (last.id && !String(last.id).startsWith("local-")) {
        try {
          await deleteAction(game.gameId, last.id);
        } catch (e) {
          // Бекенд може не підтримувати видалення дій — все одно скасовуємо
          // локально, щоб ведучий не застряг з помилковим записом.
          console.error(e);
        }
      }

      setGame((prev) => {
        const actions = prev.actions.slice(0, -1);
        let players = prev.players;
        if (last.type === "kill" || last.type === "vote_out") {
          players = players.map((p) =>
            p.id === last.targetId ? { ...p, alive: true } : p
          );
        }
        return { ...prev, players, actions };
      });
    } finally {
      setUndoing(false);
    }
  }

  // Розв'язує наслідки нічних вбивств цього раунду: гравець, якого записали
  // ціллю дії "kill", насправді помирає лише тут — і лише якщо його не
  // врятував лікар ("heal") на ту саму ціль того самого раунду. До цього
  // моменту вбита вночі ціль лишається "живою" в стані гри, щоб лікар (чи
  // будь-яка інша нічна дія) все ще міг обрати її.
  function resolveNightDeaths() {
    const roundActions = game.actions.filter((a) => Number(a.round) === Number(round));
    const healedIds = new Set(
      roundActions
        .filter((a) => a.type === "heal" && a.targetId != null)
        .map((a) => String(a.targetId))
    );
    const killedIds = new Set(
      roundActions
        .filter((a) => a.type === "kill" && a.targetId != null)
        .map((a) => String(a.targetId))
        .filter((id) => !healedIds.has(id))
    );
    if (killedIds.size === 0) return;

    setGame((prev) => ({
      ...prev,
      players: prev.players.map((p) =>
        killedIds.has(String(p.id)) ? { ...p, alive: false } : p
      ),
    }));

    // Персистимо смерть на бекенді для кожної реальної жертви ночі (не
    // критично, якщо запит впаде — локальний стан вже оновлено вище).
    killedIds.forEach((id) => {
      updatePlayer(game.gameId, id, { alive: false }).catch((e) => console.error(e));
    });
  }

  function stepRound(delta) {
    setRound((prev) => {
      const next = Math.max(1, prev + delta);
      setRoundInput(String(next));
      return next;
    });
  }

  function handleRoundInputChange(e) {
    const raw = e.target.value;

    // Дозволяємо порожнє поле під час редагування — не форсимо назад "1",
    // інакше наступна введена цифра просто дописується до неї.
    if (raw === "") {
      setRoundInput("");
      return;
    }

    // Приймаємо лише цифри.
    if (!/^\d+$/.test(raw)) return;

    setRoundInput(raw);
    const n = Number(raw);
    if (n >= 1) setRound(n);
  }

  function handleRoundInputBlur() {
    if (roundInput === "" || Number(roundInput) < 1) {
      setRound(1);
      setRoundInput("1");
    } else {
      // Прибираємо зайві нулі на початку тощо.
      setRoundInput(String(Number(roundInput)));
    }
  }

  // Скіп дня — на випадок, коли голосуванням нікого не вигнали. Повторює
  // той самий перехід, що й вигнання гравця вдень: перемикає на ніч,
  // готує автопідстановку кроку нічної черги і збільшує раунд.
  function handleSkipDay() {
    setPhase("night");
    setAutoNightType(true);
    stepRound(1);
  }

  function requestEndGame() {
    setConfirmState({
      title: "Завершити гру",
      message: "Завершити цю гру? Журнал і стан гравців стануть недоступні після виходу.",
      tone: "danger",
      confirmLabel: "Завершити",
      onConfirm: () => {
        setConfirmState(null);
        performEndGame();
      },
    });
  }

  async function performEndGame() {
    try {
      await endGame(game.gameId);
    } catch (e) {
      // навіть якщо бекенд недоступний, дозволяємо вийти локально
      console.error(e);
    }
    onEndGame();
  }

  return (
    <div className="screen game-screen">
      <header className="game-header">
        <div>
          <p className="eyebrow-mark">Гра {game.gameId}</p>
          <h1>За столом {alive.length} з {game.players.length}</h1>
          {alive.length > 0 && (
            <p className="side-summary">
              {SIDE_LABELS.town}: {townCount} · {SIDE_LABELS.mafia}: {mafiaCount}
              {soloCount > 0 && <> · {SIDE_LABELS.solo}: {soloCount}</>}
            </p>
          )}
        </div>

        {elapsedSeconds !== null && (
          <div className="game-timer" title="Час з початку гри">
            <span className="game-timer-label">Триває</span>
            <span className="game-timer-value">{formatElapsed(elapsedSeconds)}</span>
          </div>
        )}

        <div className="round-controls">
          <label className="field field-inline">
            <span>Раунд</span>
            <div className="round-stepper">
              <button
                type="button"
                className="btn btn-ghost btn-stepper"
                onClick={() => stepRound(-1)}
                disabled={round <= 1}
                aria-label="Попередній раунд"
              >
                −
              </button>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="text-input text-input-small round-input"
                value={roundInput}
                onChange={handleRoundInputChange}
                onFocus={(e) => e.target.select()}
                onBlur={handleRoundInputBlur}
              />
              <button
                type="button"
                className="btn btn-ghost btn-stepper"
                onClick={() => stepRound(1)}
                aria-label="Наступний раунд"
              >
                +
              </button>
            </div>
          </label>
          <div className="phase-toggle">
            <button
              className={`btn btn-toggle ${phase === "night" ? "btn-toggle-active" : ""}`}
              onClick={() => {
                setPhase("night");
                setAutoNightType(true);
              }}
            >
              Ніч
            </button>
            <button
              className={`btn btn-toggle ${phase === "day" ? "btn-toggle-active" : ""}`}
              onClick={() => {
                if (phase === "night") resolveNightDeaths();
                setPhase("day");
              }}
            >
              День
            </button>
          </div>
          {phase === "day" && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleSkipDay}
              title="Нікого не вигнали — перейти до ночі та наступного раунду"
            >
              Скіп дня
            </button>
          )}
        </div>
      </header>

      {pickField && (
        <p className="pick-hint">
          Оберіть {pickField === "actor" ? "виконавця" : "ціль"} — тапніть картку гравця нижче.{" "}
          <button type="button" className="pick-hint-cancel" onClick={() => setPickField(null)}>
            Скасувати
          </button>
        </p>
      )}

      {error && <p className="error-text">{error}</p>}

      <div className="game-layout">
        <section className="game-column" ref={aliveBoardRef}>
          <h2>Гравці за столом</h2>
          <PlayerBoard
            players={alive}
            onKick={requestKick}
            busyPlayerId={busyPlayerId}
            pickField={pickField}
            pickCandidateIds={pickCandidateIds}
            selectedActorId={draft.actorId}
            selectedTargetId={draft.targetId}
            onPickPlayer={handlePlayerPick}
          />

          {dead.length > 0 && (
            <>
              <h2 className="game-column-subheading">Поза грою</h2>
              <PlayerBoard players={dead} onKick={requestKick} busyPlayerId={busyPlayerId} />
            </>
          )}
        </section>

        <section className="game-column">
          {phase === "night" ? (
            <>
              <NightOrder players={game.players} actions={game.actions} round={round} />
              <ActionForm
                players={game.players}
                actions={game.actions}
                round={round}
                phase={phase}
                draft={draft}
                pickField={pickField}
                onTypeChange={handleTypeChange}
                onPickRequest={handlePickRequest}
                onFieldClear={handleFieldClear}
                onSubmit={handleAction}
                submitting={submittingAction}
              />
            </>
          ) : (
            <div className="empty-state empty-state-small">
              <p>Запис нічних дій доступний тільки вночі.</p>
            </div>
          )}
        </section>

        <section className="game-column">
          <h2>Журнал гри</h2>
          <ActionLog
            players={game.players}
            actions={game.actions}
            onUndo={handleUndoLast}
            undoing={undoing}
          />
          <RoundTimer />
        </section>
      </div>

      <footer className="game-footer">
        <button className="btn btn-ghost" onClick={requestEndGame}>
          Завершити гру
        </button>
      </footer>

      {showWinModal && (
        <div className="modal-backdrop win-modal-backdrop" role="presentation">
          <div
            className={`modal-dialog win-modal win-modal-${winBanner.tone}`}
            role="alertdialog"
            aria-modal="true"
            aria-label="Підказка про завершення гри"
          >
            <p className="win-modal-eyebrow">Підказка про завершення гри</p>
            <h2 className="win-modal-title">{winBanner.text}</h2>
            <p className="modal-message">
              Це підказка на основі балансу сторін — ведучий підтверджує завершення гри сам.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDismissedWinTone(winBanner.tone)}
              >
                Продовжити гру
              </button>
              <button type="button" className="btn btn-accent" onClick={requestEndGame}>
                Завершити гру
              </button>
            </div>
          </div>
        </div>
      )}

      {checkRevealPlayer && (
        <RoleCardsModal
          players={[checkRevealPlayer]}
          initialRevealed
          title="Результат перевірки"
          onClose={() => setCheckRevealPlayer(null)}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmState)}
        title={confirmState?.title}
        message={confirmState?.message}
        tone={confirmState?.tone}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
}
